import {Component, inject, OnInit, signal} from '@angular/core';
import {Wizard} from '../../../../shared/components/wizard/wizard.model';
import {WizardComponent} from '../../../../shared/components/wizard/wizard.component';
import {CompanyWizardCompanyStepComponent} from './company-wizard-company-step/company-wizard-company-step.component';
import {CompanyWizardCountryStepComponent} from './company-wizard-country-step/company-wizard-country-step.component';
import {CompanyWizardService} from './company-wizard.service';
import {CompanyWizardCityStepComponent} from './company-wizard-city-step/company-wizard-city-step.component';
import {CompanyWizardOfficeStepComponent} from './company-wizard-office-step/company-wizard-office-step.component';
import {WizardService} from '../../../../shared/components/wizard/wizard.service';
import {ActivatedRoute, Router} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {
  City,
  CityStepData,
  CompanyStepData,
  Country,
  CountryStepData,
  Office,
  OfficeStepData,
} from './company-wizard.types';
import {CompanyResponse} from '@frontend/shared-api';
import {ToastService} from '@frontend/toast';

@Component({
  selector: 'app-company-wizard',
  imports: [WizardComponent],
  templateUrl: './company-wizard.component.html',
  styleUrl: './company-wizard.component.scss',
  providers: [
    {
      provide: WizardService,
      useExisting: CompanyWizardService,
    },
  ],
})
export class CompanyWizardComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly route = inject(ActivatedRoute);
  private readonly companyWizardService = inject(CompanyWizardService);

  wizard = signal<Wizard>({
    title: 'company.title',
    backUrl: 'company',
    steps: [
      {
        index: 1,
        header: 'company.step.company',
        content: CompanyWizardCompanyStepComponent,
        isValid: false,
      },
      {
        index: 2,
        header: 'company.step.countries',
        content: CompanyWizardCountryStepComponent,
        isValid: false,
      },
      {
        index: 3,
        header: 'company.step.cities',
        content: CompanyWizardCityStepComponent,
        isValid: false,
      },
      {
        index: 4,
        header: 'company.step.offices',
        content: CompanyWizardOfficeStepComponent,
        isValid: false,
      },
    ],
    onComplete: async () => {
      const request = this.companyWizardService.buildFinalRequest();
      try {
        if (this.companyWizardService.isEdit()) {
          await firstValueFrom(this.companyWizardService.updateCompany(request));
          await this.toast.showSuccess(
            'wizard.company.toast.success.updateSummary',
            'wizard.company.toast.success.detail',
          );
        } else {
          await firstValueFrom(this.companyWizardService.createCompany(request));
          await this.toast.showSuccess(
            'wizard.company.toast.success.summary',
            'wizard.company.toast.success.detail',
          );
        }
      } catch {
        await this.router.navigate([this.wizard().backUrl]);
      }
      await this.router.navigate([this.wizard().backUrl]);
    },
  });

  ngOnInit(): void {
    this.route.data.subscribe(({company}) => {
      if (company) {
        const {companyData, countryData, cityData, officeData} =
          this.mapCompanyResponseToInternal(company);

        this.companyWizardService.updateStepData({
          stepIndex: 1,
          data: companyData,
        });
        this.companyWizardService.updateStepData({
          stepIndex: 2,
          data: countryData,
        });
        this.companyWizardService.updateStepData({
          stepIndex: 3,
          data: cityData,
        });
        this.companyWizardService.updateStepData({
          stepIndex: 4,
          data: officeData,
        });

        this.companyWizardService.isEdit.set(true);
      }
    });
  }

  private mapCompanyResponseToInternal(company: CompanyResponse): {
    companyData: CompanyStepData;
    countryData: CountryStepData;
    cityData: CityStepData;
    officeData: OfficeStepData;
  } {
    const companyData: CompanyStepData = {
      id: company.id,
      company: company.name,
      website: company.website,
      street: company.street,
      city: company.city,
      zip: company.zip,
    };

    const countries: Country[] = company.countries.map((countryResp) => ({
      countryCode: countryResp.countryCode,
      cities: countryResp.cities.map((cityResp) => ({
        uuid: String(cityResp.id),
        name: cityResp.name,
        isDeletable: cityResp.isDeletable,
        offices: cityResp.offices.map((officeResp) => ({
          name: officeResp.name,
          address: officeResp.address,
          isDeletable: officeResp.isDeletable,
        })),
      })),
      isDeletable: countryResp.isDeletable,
    }));

    const countryData: CountryStepData = {countries};

    const citiesMapping: Record<string, City[]> = countries.reduce(
      (acc, country) => {
        acc[country.countryCode] = country.cities;
        return acc;
      },
      {} as Record<string, City[]>,
    );
    const cityData: CityStepData = {cities: citiesMapping};

    const officesMapping: Record<string, Office[]> = countries.reduce(
      (acc, country) => {
        country.cities.forEach((city) => {
          acc[city.uuid] = city.offices;
        });
        return acc;
      },
      {} as Record<string, Office[]>,
    );
    const officeData: OfficeStepData = {offices: officesMapping};

    return {companyData, countryData, cityData, officeData};
  }
}
